import React, { useState } from 'react';
import { Modal, Form, Input, Button, message, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { authService } from '../../services/authService';
import { useTranslation } from 'react-i18next';
import styles from './AdminResetPasswordModal.module.css';

const { Text } = Typography;

interface AdminResetPasswordModalProps {
  visible: boolean;
  onCancel: () => void;
  userId: number | null;
  userEmail: string;
  userType: 'agency' | 'subscriber';
}

const AdminResetPasswordModal: React.FC<AdminResetPasswordModalProps> = ({ 
  visible, 
  onCancel, 
  userId, 
  userEmail,
  userType 
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const handleResetPassword = async (values: { newPassword: string; confirmPassword: string }) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error(t('admin.resetPassword.passwordsMismatch'));
      return;
    }

    if (!userId) {
      message.error(t('admin.resetPassword.noUserSelected'));
      return;
    }

    try {
      setLoading(true);
      await authService.adminResetPassword(userId, values.newPassword);
      message.success(
        userType === 'agency'
          ? t('admin.resetPassword.successAgency')
          : t('admin.resetPassword.successSubscriber')
      );
      handleCancel();
    } catch (error) {
      message.error(t('admin.resetPassword.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={`${t('admin.resetPassword.title')} - ${userType === 'agency' ? t('admin.resetPassword.agencyType') : t('admin.resetPassword.subscriberType')}`}
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      width={500}
    >
      <div className={styles.userInfo}>
        <Text strong>{t('admin.resetPassword.userLabel')}</Text> {userEmail}
      </div>
      
      <Text type="secondary" className={styles.descriptionText}>
        {t('admin.resetPassword.description')}
      </Text>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleResetPassword}
      >
        <Form.Item
          name="newPassword"
          label={t('admin.resetPassword.newPasswordLabel')}
          rules={[
            { required: true, message: t('admin.resetPassword.newPasswordRequired') },
            { min: 8, message: t('admin.resetPassword.passwordMinLength') },
            { 
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d\s])\S+$/,
              message: t('admin.resetPassword.passwordComplexity')
            }
          ]}
          hasFeedback
        >
          <Input.Password 
            prefix={<LockOutlined />}
            placeholder={t('admin.resetPassword.newPasswordPlaceholder')}
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label={t('admin.resetPassword.confirmPasswordLabel')}
          dependencies={['newPassword']}
          rules={[
            { required: true, message: t('admin.resetPassword.confirmPasswordRequired') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('admin.resetPassword.passwordsMismatch')));
              },
            }),
          ]}
          hasFeedback
        >
          <Input.Password 
            prefix={<LockOutlined />}
            placeholder={t('admin.resetPassword.confirmPasswordPlaceholder')}
            size="large"
          />
        </Form.Item>

        <Form.Item className={styles.formButton}>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading}
            size="large"
            block
            danger
          >
            {t('admin.resetPassword.resetButton')}
          </Button>
        </Form.Item>

        <Form.Item className={styles.cancelButton}>
          <Button 
            onClick={handleCancel}
            size="large"
            block
          >
            {t('common.cancel')}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AdminResetPasswordModal;
